import { Template } from "../helpers/types";
import api from "./api";

export interface GrafanaBarDataPoint {
  time: string;
  satellites: number;
}

export interface GrafanaFieldsValues {
  metering_point_id: number | null;
  time: number | string | null;
  simple_value: number | null;
  complex_value: string | null;
  code: string | null;
  rn: number | null;
}

export interface GroupedData {
  metering_point_id: number | null;
  code: string | null;
  data: {
    time: (number | string | null)[];
    simple_value: (number | null)[];
    complex_value: (string | null)[];
    rn: (number | null)[];
  };
  template?: Template;
}

export const formatTime = (time: string | number) => {
  const date = new Date(time);
  const pad = (n: number) => n.toString().padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${hours}:${minutes}:${seconds}\n${year}-${month}-${day}`;
};

export const extractAvg = (jsonString: string | null): number => {
  if (!jsonString) return 0;

  try {
    const obj = JSON.parse(jsonString);
    if (obj && typeof obj.avg === "number") {
      return obj.avg;
    }
    return 0;
  } catch (err) {
    console.error("Invalid JSON:", err);
    return 0;
  }
};

export const groupByMeteringPoint = (
  arr: GrafanaFieldsValues[]
): GroupedData[] => {
  const grouped: Record<number | string, GroupedData> = {};

  arr.forEach(
    ({
      metering_point_id,
      time,
      simple_value,
      complex_value,
      code,
      rn,
    }) => {
      const key = metering_point_id ?? "null";

      if (!grouped[key]) {
        grouped[key] = {
          metering_point_id,
          code,
          data: {
            time: [],
            simple_value: [],
            complex_value: [],
            rn: [],
          },
        };
      }

      grouped[key].data.time.push(time);
      grouped[key].data.simple_value.push(simple_value);
      grouped[key].data.complex_value.push(complex_value);
      grouped[key].data.rn.push(rn);
    }
  );

  return Object.values(grouped);
};

export const fetchGrafanaData = async (name: string, from: Date, to: Date): Promise<any[]> => {
  // const startDate = "2026-01-01T00:00:00.000Z"
  // const endDate = "2026-01-08T23:59:59.000Z"
  
  // const now = new Date();
  const endDate: string = to.toISOString();
  const startDate: string = from.toISOString();
  try {
    const response = await api.post<any>("/chart/bar-data", {
      name: name,
      startDate,
      endDate
    });
    const frames = response.data?.results?.A?.frames ?? [];

    if (frames.length === 0) return [];

    const frame = frames[0];
    const rawTimes: string[] = frame?.data?.values?.[1] ?? [];

    const transformed: GrafanaFieldsValues[] = rawTimes
      .map((time, i) => ({
        metering_point_id: Number(frame?.data?.values?.[0][i]) ?? null,
        rawTime: time,
        simple_value: Number(frame?.data?.values?.[2][i]) ?? null,
        complex_value: frame?.data?.values?.[3][i] ?? null,
        code: frame?.data?.values?.[4][i] ?? null,
        rn: Number(frame?.data?.values?.[5][i]) ?? null,
      }))
      .sort((a, b) => {
        const t1 =
          typeof a.rawTime === "number"
            ? a.rawTime
            : new Date(a.rawTime).getTime();
        const t2 =
          typeof b.rawTime === "number"
            ? b.rawTime
            : new Date(b.rawTime).getTime();
        return t1 - t2;
      })
      .map((item) => ({
        ...item,
        time: formatTime(item.rawTime),
        rawTime: undefined,
      }));

    const data: GroupedData[] = groupByMeteringPoint(transformed);
    return data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.errors?.server || "Invalid data");
    } else if (error.request) {
      throw new Error("No response from server. Please try again.");
    } else {
      throw new Error("Failed to fetch chart data. Please try again.");
    }
  }
};
